import { itemSearchText } from "../domain/items/search-fields";
import { studyRows } from "./study-query";
import { sopSearchRows } from "./sop-query";
import { liveQuery } from "dexie";
import type { NookDatabase } from "../infrastructure/database/database";
import { visibleContent } from "./content-service";
import {
  contentKinds,
  contentPath,
  contentTitle,
} from "../domain/content/model";
import { itemPath, type Kind } from "../domain/items/model";
export interface SearchDocument {
  id: string;
  entityType: string;
  entityId: string;
  revision: number;
  title: string;
  body: string;
  tags: string[];
  date: string;
  parent: string;
  archived: boolean;
  draft: boolean;
  path: string;
  normalized: string;
  grams: string[];
  positions: number[];
  indexVersion: number;
}
import { indexText, SEARCH_INDEX_VERSION } from "../domain/content/search-text";
export { indexText } from "../domain/content/search-text";
export async function indexSources(db: NookDatabase) {
  return db.transaction(
    "r",
    db.tables.filter((t) => t.name !== "searchDocuments"),
    async () => {
      await db.meta.get("searchDirty");
      const output: Omit<
          SearchDocument,
          "normalized" | "grams" | "positions" | "indexVersion"
        >[] = [],
        images = new Map(
          (await db.attachments.toArray()).map((a) => [a.id, a.name]),
        ),
        projects = new Map(
          (await db.projects.toArray()).filter(p=>!p.deletedAt).map((p) => [p.id, p.title]),
        );
      const add = (
        id: string,
        entityType: string,
        entityId: string,
        revision: number,
        title: string,
        body: string,
        tags: string[],
        date: string,
        parent: string,
        archived: boolean,
        draft: boolean,
        path: string,
        attachmentIds: string[],
      ) =>
        output.push({
          id,
          entityType,
          entityId,
          revision,
          title,
          body:
            body +
            " " +
            attachmentIds.map((id) => images.get(id) ?? "").join(" "),
          tags,
          date,
          parent,
          archived,
          draft,
          path,
        });
      for (const kind of contentKinds) {
        if (
          kind === "diary" &&
          (await db.settings.get("diarySearch"))?.value === "excluded"
        )
          continue;
        for (const { item, draft, tags } of await visibleContent(db, kind))
          add(
            draft?.id ?? item.id,
            kind,
            item.id,
            draft?.revision ?? item.revision,
            contentTitle(item),
            [
              item.body,
              "reason" in item
                ? item.reason + " " + item.choice
                : "url" in item
                  ? item.url + " " + item.excerpt + " " + item.summary
                  : "",
            ].join(" "),
            tags,
            "diaryDate" in item ? item.diaryDate : item.updatedAt.slice(0, 10),
            "",
            !!item.archivedAt,
            !!draft,
            contentPath(kind, item.id) +
              (draft ? "/edit?draft=" + draft.id : ""),
            item.attachmentIds,
          );
      }
      const drafts = (await db.itemDrafts.toArray()).filter(
        (d) => !d.deletedAt,
      );
      for (const kind of ["task", "event", "project"] as Kind[]) {
        const rows = await db
          .table(
            kind === "task"
              ? "tasks"
              : kind === "event"
                ? "events"
                : "projects",
          )
          .toArray();
        for (const row of rows) {
          if (row.deletedAt) continue;
          const d = drafts
              .filter(
                (d) =>
                  d.kind === kind &&
                  d.targetId === row.id &&
                  d.baseRevision === row.revision,
              )
              .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)||a.id.localeCompare(b.id))[0],
            v = d?.payload ?? row;
          add(
            row.id,
            kind,
            row.id,
            d?.revision ?? row.revision,
            v.title,
            itemSearchText(v),
            [],
            v.updatedAt.slice(0, 10),
            "projectId" in v ? (projects.get(v.projectId ?? "") ?? "") : "",
            !!row.archivedAt,
            !!d,
            itemPath(kind, row.id) + (d ? "/edit?draft=" + d.id +(d.sourceCaptureId?"&capture="+d.sourceCaptureId:"") : ""),
            v.attachmentIds,
          );
        }
        for (const d of drafts.filter(
          (d) => d.kind === kind && !rows.some((r) => r.id === d.targetId),
        ))
          add(
            d.id,
            kind,
            d.targetId,
            d.revision,
            d.payload.title,
            itemSearchText(d.payload),
            [],
            d.updatedAt.slice(0, 10),
            "",
            false,
            true,
            itemPath(kind, d.targetId) + "/edit?draft=" + d.id,
            d.payload.attachmentIds,
          );
      }
      for(const d of drafts) {
        const table=d.kind==='task'?db.tasks:d.kind==='event'?db.events:db.projects;const current=await table.get(d.targetId);
        if(!current||current.deletedAt||d.baseRevision===current.revision)continue;
        add(d.id,d.kind,d.targetId,d.revision,d.payload.title,itemSearchText(d.payload),[],d.updatedAt.slice(0,10),'',!!current.archivedAt,true,itemPath(d.kind,d.targetId)+'/edit?draft='+d.id+(d.sourceCaptureId?'&capture='+d.sourceCaptureId:''),d.payload.attachmentIds);
      }
      const captures = await db.captures.toArray(),
        cd = (await db.drafts.toArray()).filter((d) => !d.deletedAt);
      for (const c of captures) {
        if (c.deletedAt) continue;
        const d = cd.find(
          (d) =>
            d.captureId === c.id &&
            d.baseRevision === c.revision &&
            d.state === "active",
        );
        add(
          c.id,
          "capture",
          c.id,
          d?.revision ?? c.revision,
          (d?.body ?? c.body).split("\n")[0],
          d?.body ?? c.body,
          [],
          c.updatedAt.slice(0, 10),
          "",
          !!c.archivedAt,
          !!d,
          d ? `/captures/${c.id}/edit?draft=${d.id}` : `/captures/${c.id}`,
          d?.attachmentIds ?? c.attachmentIds,
        );
      }
      for (const d of cd.filter(
        (d) => !captures.some((c) => c.id === d.captureId),
      ))
        add(
          d.id,
          "capture",
          d.captureId,
          d.revision,
          d.body.split("\n")[0],
          d.body,
          [],
          d.updatedAt.slice(0, 10),
          "",
          false,
          true,
          `/captures/${d.captureId}/edit?draft=${d.id}`,
          d.attachmentIds,
        );
      for(const d of cd) {
        const c=captures.find(c=>c.id===d.captureId);if(!c||c.deletedAt||d.state==='active'&&d.baseRevision===c.revision)continue;
        add(d.id,'capture',d.captureId,d.revision,d.body.split('\n')[0],d.body,[],d.updatedAt.slice(0,10),'',!!c.archivedAt,true,'/captures/'+d.captureId+'/edit?draft='+d.id,d.attachmentIds);
      }
      for (const r of [...(await sopSearchRows(db)), ...(await studyRows(db))])
        add(
          r.id,
          r.kind.split(" ")[0],
          r.id,
          r.revision,
          r.title,
          r.body,
          [],
          r.date,
          "",
          r.archived,
          r.draft,
          r.path,
          [],
        );
      return output;
    },
  );
}
function workerForIndex() {
  return new Worker(
    new URL("../infrastructure/search/index.worker.ts", import.meta.url),
    { type: "module" },
  );
}
function sourceSignature(
  row:
    | Omit<
        SearchDocument,
        "normalized" | "grams" | "positions" | "indexVersion"
      >
    | SearchDocument,
) {
  return JSON.stringify([
    row.id,
    row.entityType,
    row.entityId,
    row.revision,
    row.title,
    row.body,
    row.tags,
    row.date,
    row.parent,
    row.archived,
    row.draft,
    row.path,
  ]);
}
export function documentText(row: Pick<SearchDocument,'title'|'body'|'tags'|'parent'|'date'>) { return [row.title,row.body,row.tags.join(' '),row.parent,row.date].join('\n'); }
export function indexIsValid(row: SearchDocument, source: Parameters<typeof sourceSignature>[0]) {
  try { if(row.indexVersion!==SEARCH_INDEX_VERSION||sourceSignature(row)!==sourceSignature(source))return false;
    const expected=indexText(documentText(source));
    return row.normalized===expected.normalized&&JSON.stringify(row.positions)===JSON.stringify(expected.positions)&&JSON.stringify(row.grams)===JSON.stringify(expected.grams);
  }catch{return false;}
}
async function installIndex(db:NookDatabase, result:SearchDocument[]) {
  return db.transaction('rw',db.tables,async()=>{
    const current=await indexSources(db),byId=new Map(current.map(r=>[r.id,r]));
    if(result.length!==current.length||new Set(result.map(r=>r.id)).size!==result.length||result.some(r=>!byId.has(r.id)||!indexIsValid(r,byId.get(r.id)!)))throw Error('索引来源已有变化，正文保留；请重试重建。');
    await db.searchDocuments.clear();await db.searchDocuments.bulkPut(result);
    await db.meta.put({key:'searchIndexVersion',value:SEARCH_INDEX_VERSION});await db.meta.put({key:'searchDirty',value:0});return result.length;
  });
}
export async function searchIndexHealth(db:NookDatabase) {
 const sources=await indexSources(db);try{const cache=await db.searchDocuments.toArray(),byId=new Map(cache.map(r=>[r.id,r]));return cache.length===sources.length&&sources.every(r=>byId.has(r.id)&&indexIsValid(byId.get(r.id)!,r))?'ready' as const:'stale' as const;}catch{return 'unavailable' as const;}
}
export async function rebuildSearchIndex(db: NookDatabase) {
 const rows=await indexSources(db),worker=workerForIndex();try{
   const result=await new Promise<SearchDocument[]>((resolve,reject)=>{worker.onmessage=e=>{if(!Array.isArray(e.data?.rows))reject(Error('索引返回无效，正文保留。'));else resolve(e.data.rows);};worker.onerror=()=>reject(Error('索引未完成，正文仍可搜索；请重试。'));worker.postMessage({generation:1,rows});});
   return await installIndex(db,result);
 }finally{worker.terminate();}
}
export function startSearchIndexer(db:NookDatabase) {
 let stopped=false,generation=0;let worker:Worker;
 try{worker=workerForIndex();}catch{return()=>{};}
 const subscription=liveQuery(()=>indexSources(db)).subscribe({next:rows=>{
   const version=++generation;
   void db.searchDocuments.toArray().then(cache=>{
     if(stopped||version!==generation)return;
     const byId=new Map(cache.map(r=>[r.id,r]));
     if(cache.length!==rows.length||rows.some(r=>!byId.has(r.id)||!indexIsValid(byId.get(r.id)!,r)))worker.postMessage({generation:version,rows});
   }).catch(()=>{/* Authority remains searchable even if cache cannot be read. */});
 },error:()=>{}});
 worker.onmessage=e=>{if(stopped||e.data?.generation!==generation||!Array.isArray(e.data.rows))return;void installIndex(db,e.data.rows).catch(()=>{});};
 worker.onerror=()=>{};
 return()=>{stopped=true;subscription.unsubscribe();worker.terminate();};
}
